import React, { useMemo } from "react";
import {
  AppBar, Dialog, IconButton, Toolbar, Typography, Slide,
  Button, Box
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { useSelector } from "react-redux";
import moment from 'moment';
import currency from "currency.js";

import { getAnnexUrl, buildChargeAccountDataSource, isExecutablePeriod } from "../../functions";
import RichEditor from "../generic/rich-editor";
import { NEWS_TYPES } from "./ContractNewsForm";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ChargeAccountTemplate = ({
  contract, payment, getContractFieldWithNews, open, onClose
}) => {
  const users = useSelector((state) => state.user.users);
  const [richEditor, setRichEditor] = React.useState(null);

  const {
    dataSource,
    paymentPlan,
    getAccountDate,
  } = useMemo(() => {
    return buildChargeAccountDataSource({ contract, payment, users, getContractFieldWithNews });
  }, [contract, payment, users, getContractFieldWithNews]);

  const getImageSize = async (url) => {
    try {
      const getImageDimensions = (imageUrl) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = (error) => {
            console.error('Error loading image for dimensions:', error);
            reject(error);
          };
          img.src = imageUrl;
        });
      };

      const { width: originalWidth, height: originalHeight } = await getImageDimensions(url);

      let targetHeight = 2500;
      let aspectRatio = originalWidth / originalHeight;
      let targetWidth = targetHeight * aspectRatio;

      if (originalWidth > originalHeight) {
        targetWidth = 4500;
        aspectRatio = originalHeight / originalWidth;
        targetHeight = targetWidth * aspectRatio;
      }

      return { width: Math.round(targetWidth), height: Math.round(targetHeight) };
    } catch (error) {
      console.error('Error al procesar imagen:', error);
    }
  };

  const handleDocumentLoaded = async (editor) => {
    const activities = [];
    const paymentActivities = contract.payments?.[payment]?.activities || [];
    for (let act of paymentActivities) {
      const annexes = [];
      for (let annx of act.annexes || []) {
        let size = undefined;

        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(annx.url);
        if (isImage) size = await getImageSize(annx.url);

        annexes.push({ ...annx, size });
      }
      if (annexes.length > 0) {
        activities.push({ title: act.title, annexes });
      }
    }

    setRichEditor(editor);
    const table0 = editor.document.tables.getByIndex(0)?._table;

    if (table0) {
      const payments = [];
      for (let i = 0; i <= parseInt(payment.split('_')[1], 10); i++) {
        // Un mes que una suspensión dejó en cero no es un periodo de pago: no
        // ocupa fila en la tabla de ejecución. Aportaba 0 al acumulado, así
        // que omitirlo no cambia los totales y las filas quedan consecutivas.
        if (!isExecutablePeriod(paymentPlan, i)) continue;
        const pay = paymentPlan[i];
        const accrued = payments.map((p) => p.pay).reduce((a, v) => a + v, 0) + pay;
        const accountDate = getAccountDate(i);
        const contractBudget = getContractFieldWithNews('contractBudget', accountDate);
        const porcentage = (pay / contractBudget) * 100;
        const pending = contractBudget - accrued;

        const news = contract.news
          ?.map((n, idx) => ({ ...n, idx }))
          .filter(n => {
            if (!n.startedAt) return false;

            const monthStart = moment(accountDate).startOf('month');
            const monthEnd = moment(accountDate).endOf('month');
            const startsBeforeMonthEnd = moment(n.startedAt).isSameOrBefore(monthEnd);

            if (!n.finishedAt) {
              return moment(n.startedAt).isSameOrAfter(monthStart) &&
                     moment(n.startedAt).isSameOrBefore(monthEnd);
            }

            const endsAfterMonthStart = moment(n.finishedAt).isSameOrAfter(monthStart);

            return startsBeforeMonthEnd && endsAfterMonthStart;
          })
          .map(n => `N${n.idx + 1} - ${NEWS_TYPES[n.type]}`)
          .join(', ') || '';

        payments.push({
          budget: contractBudget,
          pay,
          porcentage,
          accrued,
          pending,
          accrued_porcentage: payments.map((p) => p.porcentage).reduce((a, v) => a + v, 0) + porcentage,
          news
        });
      }

      const initRow = 25;
      payments.forEach((payment, idx) => {
        const cells = table0.rows[initRow + idx].cells;
        editor.document.insertText(cells[1].interval.start, currency(payment.budget, { precision: 0 }).format());
        editor.document.insertText(cells[2].interval.start, currency(payment.pay, { precision: 0 }).format());
        editor.document.insertText(cells[3].interval.start, `${payment.porcentage.toFixed(2)}%`);
        editor.document.insertText(cells[4].interval.start, currency(payment.accrued, { precision: 0 }).format());
        editor.document.insertText(cells[5].interval.start, currency(payment.pending, { precision: 0 }).format());
        editor.document.insertText(cells[6].interval.start, `${payment.accrued_porcentage.toFixed(2)}%`);
        editor.document.insertText(cells[7].interval.start, payment.news);
      });

      setTimeout(() => {
        editor.mailMergeOptions.viewMergedData = true;
        editor.readOnly = true;
      }, 500);
    }

    const table2 = editor.document.tables.getByIndex(2)?._table;

    const cell = table2.rows[6].cells[0];
    let startPos = cell.interval.start;
    let imagesWidth = 0;

    activities.forEach((activity) => {
      if (startPos > 8000) return;

      const title = `\n(${activity.title.split('.')[0]}): `;
      editor.document.insertText(startPos, title);
      startPos += title.length;

      activity.annexes.forEach((annex) => {
        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(annex.url);
        if (isImage && annex.size && imagesWidth + annex.size.width < 14500) {
          editor.document.insertPicture(cell.interval.start, annex.url, annex.size);
          imagesWidth += annex.size.width;
          startPos += 1;
        } else {
          let text = `${annex.name}`;
          editor.document.insertText(startPos, `${text}`);

          const hplink = new DevExpress.RichEdit.HyperlinkInfo(annex.name, annex.url, undefined, annex.name); // eslint-disable-line
          const link = editor.selection.activeSubDocument.hyperlinks.create(
            { start: startPos, length: text.length }, hplink
          );

          startPos = link.interval.end;

          text = ` (URL: ${getAnnexUrl(contract.contractCode, annex)}) - `;
          editor.document.insertText(startPos, text);
          startPos += text.length;
        }
      });
    });
  };

  const file = useMemo(() => {
    if (!payment) return null;

    const tmpFile = {
      name: 'L2ZtQv6TSbBcBNM2',
      path: 'templates/L2ZtQv6TSbBcBNM2',
      url: `${process.env.REACT_APP_DOCUMENTS_URL}%2Ftemplates%2FL2ZtQv6TSbBcBNM2?alt=media&token=948d649f-c536-4294-9c11-24b3c24ce95a`
    };
    if ((parseInt(payment.split('_')[1], 10) + 1) > 12) {
      tmpFile.name += '_EXT';
      tmpFile.path += '_EXT';
      tmpFile.url = `${process.env.REACT_APP_DOCUMENTS_URL}%2Ftemplates%2FL2ZtQv6TSbBcBNM2_EXT?alt=media&token=8ba5deb5-985d-4e94-9f3c-d2c195d5aed4`;
    }

    return tmpFile;
  }, [payment]);

  return (
    <Dialog
      disableEscapeKeyDown
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Cuenta de cobro
          </Typography>
          {contract.payments && contract.payments[payment]?.approved
            && <Box sx={{ alignItems: 'center', display: 'flex' }}>
              <PrintIcon />
              <Button sx={{ color: 'white' }} onClick={() => {
                if (richEditor) {
                  richEditor.printDocument();
                }
              }}>
                Imprimir
              </Button>

            </Box>
          }
        </Toolbar>
      </AppBar>
      {file
        && <RichEditor
          noRibbon
          noSave
          watermark="BORRADOR"
          file={file}
          onDocumentLoaded={handleDocumentLoaded}
          dataSource={dataSource}
        />
      }
    </Dialog>
  );
};

export default ChargeAccountTemplate;
